// @ts-nocheck

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import clone from 'lodash/clone';
import map from 'lodash/map';
import { yieldCallback } from '@zillow/yield-callback';

// Components
import Button from 'app/shared/core/Button';
import ContactCallToAction from 'app/shared/modules/photo-gallery/ContactCallToAction';
import ImageLoader from 'app/shared/modules/photo-gallery/ImageLoader';
import PhotoCarousel from 'app/shared/modules/PhotoCarousel';

// Styles
import {
  photoGalleryContainer,
  headerArea,
  carouselArea,
  infoArea,
  infoAreaMobileModelFloorplan,
  footerArea,
  contactButton,
  contactIcon,
  infoLink,
  imageCaption,
  photoNav,
  photoNavList,
  photoThumbWrapper,
  photoThumb,
  overlay,
  activeThumbWrapper,
  hoverThumbWrapper,
  floorplanButtonContainer,
  floorplanButton,
  floorplanButtonCaret,
  tours3dButtonTop,
  tours3dButtonBottom,
  tours3dIcon,
  imageCache,
  photoImage,
  StyledPhotoCountText,
  mobileHeader,
  mobileHeaderLeft,
  mobileHeaderCenter,
  mobileHeaderRight,
  mobileFooter,
  desktopFooter,
  morePhotosIndicator,
  morePhotosArrow,
  morePhotosText,
} from './PhotoGalleryFull.styles';

// Misc / Utils
import { listingUtils_getListingImgAltText, listingUtils_getListingImgTitleText } from 'app/shared/utils/listingUtils';
import controller from './controller';
import Icon3DGlasses from 'images/icons/3d-glasses-white.svg';
import IconArrowRight from 'images/icons/arrow-right-white.svg';
import IconMail from 'images/icons/mail-white.svg';
import noPhoto from 'images/noPhoto.png';

const PHOTO_THUMB_WIDTH = 80;

interface Photo {
  url: string;
  caption: string;
  modelId?: string;
  galleryidx?: number;
}

interface PhotoGalleryFullProps {
  defaultCurrentIndex?: number;
  onChange?: (index: number) => void;
  onClickContact?: () => void;
  onClickOpenFdp?: (params: { fdpName: string; source: string; params: { modelId: string } }) => void;
  onPhotoGalleryTo3dTours?: () => void;
  photoCount?: number;
  photos?: Photo[];
  screenWidth?: string;
  showContactUpsell?: boolean;
  thumbPhotos?: Photo[];
  has3dTour?: boolean;
  floorplanModelId?: string;
  displayName?: string;
  listingAddress?: any;
  hideHeaderPhotoCount?: boolean;
}

/**
 * This component displays listing photos (+ floorplans and 3D tour if available) in a scrollable carousel.
 * It is used for
 *   - the expanded photo view (via photo gallery) for DESKTOP ONLY
 *   - the expanded floorplan view (via HDP) for MWEB + DESKTOP
 */

const PhotoGalleryFull: React.FC<PhotoGalleryFullProps> = ({
  defaultCurrentIndex = 0,
  onChange = () => {},
  onClickContact = () => {},
  onClickOpenFdp = () => {},
  photoCount = 0,
  photos = [{ url: noPhoto, caption: '' }],
  screenWidth = '',
  showContactUpsell = false,
  thumbPhotos = [{ url: noPhoto, caption: '' }],
  has3dTour = false,
  floorplanModelId,
  displayName,
  listingAddress,
  onPhotoGalleryTo3dTours,
  hideHeaderPhotoCount = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(defaultCurrentIndex);
  const [cachedPhotos, setCachedPhotos] = useState<Photo[]>([]);
  const [checkedThumbPhotos, setCheckedThumbPhotos] = useState<Photo[]>([]);
  const [bottomNavUsableWidth, setBottomNavUsableWidth] = useState(0);
  const [shouldCenterThumbnails, setShouldCenterThumbnails] = useState(true);
  const [hasMorePhotos, setHasMorePhotos] = useState(false);

  const photoNavRef = useRef<HTMLDivElement>(null);
  const prevCurrentIndexRef = useRef<number>(currentIndex);

  const isMobile = screenWidth === 'sm' || screenWidth === 'xs';

  // Initialize cached photos on mount
  useEffect(() => {
    const initialCachedPhotos = map(photos, () => 0);
    setCachedPhotos(initialCachedPhotos as Photo[]);
  }, [photos]);

  // Initialize checked thumb photos
  useEffect(() => {
    setCheckedThumbPhotos(controller.checkPhotos(thumbPhotos));
  }, [thumbPhotos]);

  // Handle current index changes
  useEffect(() => {
    if (currentIndex !== prevCurrentIndexRef.current) {
      scrollToActiveThumbnail();
      prevCurrentIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  const checkHasMorePhotos = useCallback(() => {
    if (!photoNavRef.current) return false;

    const { scrollLeft, scrollWidth, clientWidth } = photoNavRef.current;
    // More robust tolerance calculation for different browsers and zoom levels
    const tolerance = Math.max(1, Math.ceil(clientWidth * 0.001)); // 0.1% of client width, minimum 1px
    return Math.ceil(scrollLeft + clientWidth) < scrollWidth - tolerance;
  }, []);

  // Throttle function to limit how often we update hasMorePhotos
  const throttledUpdateRef = useRef<number | null>(null);
  const scheduleHasMorePhotosCheck = useCallback(() => {
    if (throttledUpdateRef.current) {
      cancelAnimationFrame(throttledUpdateRef.current);
    }

    throttledUpdateRef.current = requestAnimationFrame(() => {
      setHasMorePhotos(checkHasMorePhotos());
      throttledUpdateRef.current = null;
    });
  }, [checkHasMorePhotos]);

  const scrollToActiveThumbnail = useCallback(() => {
    if (!photoNavRef.current) return;

    const photoNavElement = photoNavRef.current;
    const photoNavWidth = photoNavElement.offsetWidth;
    const maxVisiblePhotos = Math.floor(photoNavWidth / PHOTO_THUMB_WIDTH);

    // Update bottomNavUsableWidth for other functions
    setBottomNavUsableWidth(photoNavWidth);

    // Determine if thumbnails should be centered or allow scrolling
    const totalPhotosWidth = photos.length * PHOTO_THUMB_WIDTH;
    const shouldCenter = totalPhotosWidth <= photoNavWidth;
    setShouldCenterThumbnails(shouldCenter);

    if (photos.length <= maxVisiblePhotos) {
      // All photos fit on screen - scroll to start and center via CSS
      photoNavElement.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    // Calculate target scroll position to center the active thumbnail
    const thumbnailPosition = currentIndex * PHOTO_THUMB_WIDTH + PHOTO_THUMB_WIDTH / 2;
    const targetScrollLeft = thumbnailPosition - photoNavWidth / 2;

    // Constrain scroll position to valid range
    const maxScrollLeft = totalPhotosWidth - photoNavWidth;
    const constrainedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

    // Scroll to center the active thumbnail
    photoNavElement.scrollTo({ left: constrainedScrollLeft, behavior: 'smooth' });

    // Update hasMorePhotos after thumbnail positioning is complete
    scheduleHasMorePhotosCheck();
  }, [currentIndex, photos.length, scheduleHasMorePhotosCheck]);

  // Initialize navigation on mount and photo changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is ready
    const timer = setTimeout(() => {
      scrollToActiveThumbnail();
    }, 0);
    return () => clearTimeout(timer);
  }, [photos]);

  // Add scroll event listener for "More" photos indicator
  useEffect(() => {
    // Only setup for desktop when "More" indicator is actually shown
    if (isMobile) return;

    const photoNavElement = photoNavRef.current;
    if (!photoNavElement) return;

    // Initial checks
    scheduleHasMorePhotosCheck(); // Immediate check
    const delayedCheck = setTimeout(scheduleHasMorePhotosCheck, 100); // Delayed check

    // Add throttled scroll listener
    photoNavElement.addEventListener('scroll', scheduleHasMorePhotosCheck);

    // Add debounced ResizeObserver
    let resizeObserver: ResizeObserver | null = null;
    let resizeTimeout: number | null = null;

    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Debounce resize events
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(scheduleHasMorePhotosCheck, 100);
      });
      resizeObserver.observe(photoNavElement);
    }

    return () => {
      photoNavElement.removeEventListener('scroll', scheduleHasMorePhotosCheck);
      clearTimeout(delayedCheck);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (throttledUpdateRef.current) {
        cancelAnimationFrame(throttledUpdateRef.current);
        throttledUpdateRef.current = null;
      }
    };
  }, [isMobile, scheduleHasMorePhotosCheck, photos.length]); // Re-run when photos change

  const fetchNextImages = useCallback(
    (start?: number, end?: number) => {
      const startIndex = start || currentIndex;
      const endIndex = end || photos.length;

      // check to see if we're already maximum cache
      if (startIndex >= endIndex || startIndex >= photos.length) {
        return;
      } else if (cachedPhotos[startIndex]) {
        return;
      } else {
        const newCachedPhotos = clone(cachedPhotos);
        newCachedPhotos[startIndex] = photos[startIndex];

        setCachedPhotos(newCachedPhotos);

        // Continue fetching recursively
        setTimeout(() => {
          fetchNextImages(startIndex + 1, endIndex);
        }, 0);
      }
    },
    [currentIndex, photos, cachedPhotos],
  );

  const fetchPrevImages = useCallback(
    (start?: number, end?: number) => {
      const startIndex = start || photos.length - 1;
      const endIndex = end || 0;

      // check to see if we're already maximum cache
      if (startIndex <= endIndex || startIndex <= 0) {
        return;
      } else if (cachedPhotos[startIndex]) {
        return;
      } else {
        const newCachedPhotos = clone(cachedPhotos);
        newCachedPhotos[startIndex] = photos[startIndex];

        setCachedPhotos(newCachedPhotos);

        // Continue fetching recursively
        setTimeout(() => {
          fetchPrevImages(startIndex - 1, endIndex);
        }, 0);
      }
    },
    [photos, cachedPhotos],
  );

  const handleImageChange = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      onChange(index);
    },
    [onChange],
  );

  const handleClickThumbnail = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();

      setCurrentIndex(index);

      // Fetch adjacent images after state update
      setTimeout(() => {
        fetchNextImages(index, index + 1);
        fetchPrevImages(index, index - 1);
      }, 0);
    },
    [fetchNextImages, fetchPrevImages],
  );

  const handleOpenFdpClick = yieldCallback(() => {
    const currentPhoto = currentIndex < photos.length ? photos[currentIndex] : null;
    const isFloorplanPhoto = currentIndex >= photoCount;

    // floorplanModelId is only passed in from ModelFloorplanItem
    let modelId = floorplanModelId;
    if (!modelId) {
      modelId = isFloorplanPhoto && currentPhoto ? currentPhoto.modelId : '';
    }
    if (!modelId) {
      return;
    }

    onClickOpenFdp({
      fdpName: 'FloorPlans', // PascalCase because it is used for analytics
      source: 'PhotoGallery',
      params: { modelId },
    });
  });

  const renderPhotoNav = useCallback(() => {
    const listStyle = {
      justifyContent: shouldCenterThumbnails ? 'center' : 'flex-start',
    };

    return (
      <ul className={photoNavList} style={listStyle}>
        {checkedThumbPhotos.map((photo, index) => {
          const photoCaption = photo.caption;
          const imgAltText = listingUtils_getListingImgAltText({
            photoCaption,
            nameOrAddress: displayName,
            isPrimaryPhoto: index === 0,
          });
          const imgTitleText = listingUtils_getListingImgTitleText({
            currentIndex: index,
            maxIndex: checkedThumbPhotos.length,
            nameOrAddress: displayName,
            addressDetails: listingAddress,
          });

          const isActive = currentIndex === index;
          const wrapperClasses = cx(photoThumbWrapper, { [activeThumbWrapper]: isActive }, hoverThumbWrapper);

          return (
            <li className={wrapperClasses} key={`thumb-${index}-${photo.url}`}>
              <button className={overlay} onClick={(e) => handleClickThumbnail(index, e)}>
                <div className={photoThumb}>
                  <ImageLoader
                    className={photoImage}
                    image={photo.url}
                    key={`thumb-img-${index}-${photo.url}`}
                    altText={imgAltText}
                    titleText={imgTitleText}
                    fallbackImage={photo.isStreetView ? noPhoto : undefined}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }, [checkedThumbPhotos, currentIndex, displayName, listingAddress, handleClickThumbnail, shouldCenterThumbnails]);

  // Computed values
  const currentPhoto = currentIndex < photos.length ? photos[currentIndex] : null;
  const caption = currentPhoto ? currentPhoto.caption : '';
  const isFloorplanPhoto = currentIndex >= photoCount && currentIndex < photos.length;
  const isModelFloorplanContext = Boolean(floorplanModelId);

  // Render header content based on mobile vs desktop and usage context
  const renderHeader = () => {
    if (isMobile) {
      // Mobile layouts differ between PhotoGalleryPopupModal and ModelFloorplanItem
      // For now, render contact button - parent components can override via styling
      return (
        <div className={mobileHeader}>
          <div className={mobileHeaderLeft}>
            {onClickContact && (
              <Button btnType="dark" className={contactButton} onClick={onClickContact}>
                <img src={IconMail} className={contactIcon} alt="" />
                <span className={infoLink}>Contact now</span>
              </Button>
            )}
          </div>
          <div className={mobileHeaderCenter}>
            {!hideHeaderPhotoCount && (
              <StyledPhotoCountText>
                {currentIndex + 1} of {photos.length}
              </StyledPhotoCountText>
            )}
          </div>
          <div className={mobileHeaderRight}>{/* Close button handled by PopupModal */}</div>
        </div>
      );
    }

    // Desktop header
    return (
      <div className={headerArea}>
        {onClickContact && (
          <Button btnType="dark" className={contactButton} onClick={onClickContact}>
            <img src={IconMail} className={contactIcon} alt="" />
            <span className={infoLink}>Contact now</span>
          </Button>
        )}
        {has3dTour && (
          <button className={tours3dButtonTop} onClick={onPhotoGalleryTo3dTours}>
            <img src={Icon3DGlasses} className={tours3dIcon} alt="" />
            Switch to 3D tour
          </button>
        )}
      </div>
    );
  };

  // Render footer content based on mobile vs desktop and context
  const renderFooter = () => {
    if (isMobile) {
      if (isModelFloorplanContext) {
        // Mobile ModelFloorplanItem: Only show floorplan button (no photo nav)
        return (
          <div className={mobileFooter}>
            {isFloorplanPhoto && (
              <Button btnType="dark" className={floorplanButton} onClick={handleOpenFdpClick}>
                View details about this floor plan
                <img className={floorplanButtonCaret} src={IconArrowRight} alt="" />
              </Button>
            )}
            {has3dTour && (
              <button className={tours3dButtonBottom} onClick={onPhotoGalleryTo3dTours}>
                <img src={Icon3DGlasses} className={tours3dIcon} alt="" />
                Switch to 3D tour
              </button>
            )}
          </div>
        );
      } else {
        // Mobile PhotoGalleryPopupModal: Original behavior
        return (
          <div className={mobileFooter}>
            {!isFloorplanPhoto && onClickContact && (
              <Button btnType="primary" onClick={onClickContact}>
                Contact now
              </Button>
            )}
            {isFloorplanPhoto && (
              <Button btnType="dark" className={floorplanButton} onClick={handleOpenFdpClick}>
                View details about this floor plan
                <img className={floorplanButtonCaret} src={IconArrowRight} alt="" />
              </Button>
            )}
            {has3dTour && (
              <button className={tours3dButtonBottom} onClick={onPhotoGalleryTo3dTours}>
                <img src={Icon3DGlasses} className={tours3dIcon} alt="" />
                Switch to 3D tour
              </button>
            )}
          </div>
        );
      }
    }

    // Desktop footer
    if (isModelFloorplanContext) {
      // Desktop ModelFloorplanItem: Floorplan button → Photo count → Photo nav
      return (
        <div className={desktopFooter}>
          {isFloorplanPhoto && (
            <div className={floorplanButtonContainer}>
              <Button btnType="dark" className={floorplanButton} onClick={handleOpenFdpClick}>
                View details about this floor plan
                <img className={floorplanButtonCaret} src={IconArrowRight} alt="" />
              </Button>
            </div>
          )}
          {/* Desktop ModelFloorplan: Show photo count in footer */}
          <StyledPhotoCountText>
            {currentIndex + 1} of {photos.length}
          </StyledPhotoCountText>
          <div className={photoNav} ref={photoNavRef}>
            {renderPhotoNav()}
          </div>
          {hasMorePhotos && (
            <div className={morePhotosIndicator}>
              <span className={morePhotosArrow}>›</span>
              <span className={morePhotosText}>More</span>
            </div>
          )}
        </div>
      );
    } else {
      // Desktop PhotoGalleryPopupModal: Original behavior
      return (
        <div className={desktopFooter}>
          <div className={photoNav} ref={photoNavRef}>
            {renderPhotoNav()}
          </div>
          {hasMorePhotos && (
            <div className={morePhotosIndicator}>
              <span className={morePhotosArrow}>›</span>
              <span className={morePhotosText}>More</span>
            </div>
          )}
          {isFloorplanPhoto && (
            <div className={floorplanButtonContainer}>
              <Button btnType="dark" className={floorplanButton} onClick={handleOpenFdpClick}>
                View details about this floor plan
                <img className={floorplanButtonCaret} src={IconArrowRight} alt="" />
              </Button>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className={photoGalleryContainer}>
      {renderHeader()}

      <div className={carouselArea}>
        <PhotoCarousel
          animateChange={!(screenWidth === 'lg' || screenWidth === 'xl')}
          defaultIndex={currentIndex}
          loopable
          onChange={handleImageChange}
          showItemCount={false}
        >
          {photos.map((photo, idx) => {
            const photoCaption = photo.caption;
            const imgAltText = listingUtils_getListingImgAltText({
              photoCaption,
              nameOrAddress: displayName,
              isPrimaryPhoto: idx === 0,
            });
            const imgTitleText = listingUtils_getListingImgTitleText({
              currentIndex: idx,
              maxIndex: photos.length,
              nameOrAddress: displayName,
              addressDetails: listingAddress,
            });
            return (
              <ImageLoader
                image={photo.url}
                key={`carousel-${idx}-${photo.url}`}
                altText={imgAltText}
                titleText={imgTitleText}
                objectFit="contain"
                fallbackImage={photo.isStreetView ? noPhoto : undefined}
              />
            );
          })}
          {onClickContact && showContactUpsell && (
            <div>
              <ContactCallToAction onCtaClick={onClickContact} />
            </div>
          )}
        </PhotoCarousel>
      </div>

      <div className={isMobile && isModelFloorplanContext ? infoAreaMobileModelFloorplan : infoArea}>
        {/* Desktop: Show caption and photo count for PhotoGalleryPopupModal */}
        {currentIndex < photos.length && !isMobile && !isModelFloorplanContext && (
          <>
            <div className={imageCaption}>{caption}</div>
            <StyledPhotoCountText>
              {currentIndex + 1} of {photos.length}
            </StyledPhotoCountText>
          </>
        )}
        {/* Mobile: Show photo count for ModelFloorplanItem context */}
        {isMobile && isModelFloorplanContext && (
          <StyledPhotoCountText>
            {currentIndex + 1} of {photos.length}
          </StyledPhotoCountText>
        )}
      </div>

      {renderFooter()}

      {/* Image cache for performance */}
      <div className={imageCache}>
        {map(cachedPhotos, (cachedPhoto, i) => {
          if (cachedPhoto) {
            return <img key={`cache-${i}-${cachedPhoto.url}`} src={cachedPhoto.url} alt="" />;
          }
          return null;
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (state: Record<string, any>) => {
  return {
    displayName: state.currentListingDetails.currentListing.displayName,
    listingAddress: state.currentListingDetails.currentListing.address,
    screenWidth: state.app.device.screenWidth,
  };
};

export default connect(mapStateToProps)(PhotoGalleryFull);
